import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Zap, ArrowRight, Clock, CheckCircle, Gift, Quote } from 'lucide-react';

// Contenu par profil
const PROFILE_CONTENT = {
  'Plafonneur': {
    color: 'text-red-400',
    bgColor: 'bg-red-500/10',
    borderColor: 'border-red-500/30',
    title: "Ton score révèle quelque chose d'important…",
    subtitle: "Tu avances à l'instinct. Et c'est exactement ce qui te coûte des ventes.",
    body: [
      "Ton Score de Scalabilité™ montre que les bases ne sont pas encore posées. Pas de panique — c'est récupérable. Mais ça ne se récupère pas au hasard.",
      "Ce qui bloque ton CA, ce n'est pas ton offre en elle-même. C'est que ton client idéal n'est pas encore défini avec suffisamment de précision pour que ton message atterrisse juste… et que tes ventes deviennent prévisibles.",
      "Boost-StrategIA est conçu exactement pour ça. Il te guide pas à pas pour poser les bonnes bases — dans le bon ordre — sans te noyer dans la théorie."
    ],
    actionPlan: {
      title: "Tes 3 premières actions prioritaires",
      steps: [
        {
          title: "1. Définis ton client idéal avec précision",
          description: "Avant de vendre, tu dois savoir exactement à qui tu t'adresses. Un avatar flou = un message qui ne touche personne."
        },
        {
          title: "2. Clarifie ta promesse principale",
          description: "Quelle transformation concrète apportes-tu ? Si tu ne peux pas l'expliquer en une phrase, ton prospect ne comprendra pas non plus."
        },
        {
          title: "3. Structure une offre lisible",
          description: "Prix, livrables, durée, résultat attendu. Tout doit être clair avant de communiquer."
        }
      ]
    },
    scoreRange: '0-39'
  },
  'Dispersé': {
    color: 'text-orange-400',
    bgColor: 'bg-orange-500/10',
    borderColor: 'border-orange-500/30',
    title: "Tu as des bases… mais elles ne travaillent pas ensemble.",
    subtitle: "Ton offre dit une chose. Ton message en dit une autre. Ton plan marketing ? Il n'existe pas vraiment encore.",
    body: [
      "Ton Score de Scalabilité™ le confirme : tu n'es pas au point zéro. Tu as déjà des clients, une offre, de l'expérience.",
      "Mais quelque chose reste incohérent entre ce que tu proposes, ce que tu communiques, et ce que tu cibles. Et cette incohérence, c'est elle qui rend ton CA imprévisible d'un mois à l'autre.",
      "Boost-StrategIA t'aide à remettre tout ça en alignement. Et à construire un plan marketing sur 30 à 90 jours qui tient vraiment la route."
    ],
    actionPlan: {
      title: "Tes 3 premières actions prioritaires",
      steps: [
        {
          title: "1. Aligne ton message sur ton offre",
          description: "Vérifie que ce que tu dis sur les réseaux correspond vraiment à ce que tu vends. Incohérence = confusion = pas de vente."
        },
        {
          title: "2. Choisis UN canal d'acquisition principal",
          description: "Être partout = être nulle part. Concentre 80% de ton énergie sur le canal qui fonctionne le mieux pour toi."
        },
        {
          title: "3. Crée un plan marketing 30 jours",
          description: "Pas de plan = navigation à vue. Définis tes actions semaine par semaine avec des objectifs mesurables."
        }
      ]
    },
    scoreRange: '40-70'
  },
  'Prédictible': {
    color: 'text-green-400',
    bgColor: 'bg-green-500/10',
    borderColor: 'border-green-500/30',
    title: "Tu as une structure. C'est rare. Maintenant, il s'agit de passer à l'étape d'après.",
    subtitle: "Optimiser. Automatiser. Scaler — sans te disperser.",
    body: [
      "Ton Score de Scalabilité™ le confirme : tu es déjà dans le bon couloir. Mais avoir une structure, ce n'est pas la même chose que l'exploiter à 100 %.",
      "Il reste probablement des leviers que tu n'as pas encore activés — sur ton message, ta différenciation, ou ton plan à 90 jours.",
      "Boost-StrategIA t'aide à les identifier avec précision… et à structurer ta croissance de façon intelligente, sans t'éparpiller."
    ],
    actionPlan: {
      title: "Tes 3 prochains leviers de croissance",
      steps: [
        {
          title: "1. Optimise ton taux de conversion",
          description: "Tu as du trafic, mais convertis-tu assez ? Analyse ton tunnel et identifie les points de friction."
        },
        {
          title: "2. Systématise ce qui fonctionne",
          description: "Tes meilleures ventes suivent un pattern. Documente-le et transforme-le en process répétable."
        },
        {
          title: "3. Prépare ton plan à 90 jours",
          description: "Avec une base solide, tu peux voir plus loin. Définis tes objectifs de scaling et les ressources nécessaires."
        }
      ]
    },
    scoreRange: '75-100',
    testimonials: [
      {
        quote: "J'ai pris une claque. Le GPT m'a aidé à clarifier mon offre, mon positionnement et à identifier mes angles morts en 30 minutes. On sent qu'il y a une réelle expertise en marketing et en psychologie — ce n'est pas juste un outil générique.",
        author: "Alexis Rocco",
        role: "Fondateur Les Prompts d'Alexis / Growth Marketer"
      },
      {
        quote: "Après 6 ans et demi avec plusieurs spécialistes marketing, je voulais stabiliser mon CA. Les résultats ? +83% et +124% sur nos deux premiers lancements. Tout est devenu clair, précis, chirurgical. Je m'adresse enfin à ma clientèle idéale.",
        author: "Coach et préparateur mental",
        role: "15 ans d'expérience"
      }
    ]
  }
};

const PROMO_CODE = 'BIENVENUE20';
const TIMER_DURATION = 48 * 60 * 60 * 1000; // 48h en millisecondes
const STORAGE_KEY = 'boost_promo_timer_start';

const ThankYou = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [timeLeft, setTimeLeft] = useState(null);
  const [isExpired, setIsExpired] = useState(false);

  // Récupérer les données du diagnostic
  const { score, profile, firstName } = location.state || {};

  // Rediriger si pas de données
  useEffect(() => {
    if (!score || !profile) {
      navigate('/diagnostic');
    }
  }, [score, profile, navigate]);

  // Initialiser et gérer le timer
  useEffect(() => {
    // Récupérer ou créer le timestamp de départ
    let startTime = localStorage.getItem(STORAGE_KEY);
    
    if (!startTime) {
      startTime = Date.now().toString();
      localStorage.setItem(STORAGE_KEY, startTime);
    }

    const updateTimer = () => {
      const elapsed = Date.now() - parseInt(startTime);
      const remaining = TIMER_DURATION - elapsed;

      if (remaining <= 0) {
        setIsExpired(true);
        setTimeLeft(null);
      } else {
        setIsExpired(false);
        setTimeLeft(remaining);
      }
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);

    return () => clearInterval(interval);
  }, []);

  // Formater le temps restant
  const formatTime = (ms) => {
    if (!ms) return '00:00:00';
    
    const totalSeconds = Math.floor(ms / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  if (!score || !profile) {
    return null;
  }

  const content = PROFILE_CONTENT[profile] || PROFILE_CONTENT['Dispersé'];

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="fixed top-0 w-full z-50 glass border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex justify-between items-center">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-primary rounded-md flex items-center justify-center">
              <Zap className="w-5 h-5 sm:w-6 sm:h-6 text-black" />
            </div>
            <span className="text-lg sm:text-xl font-heading font-bold">Boost-StrategIA</span>
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-24 pb-16 px-4 sm:px-6">
        <div className="max-w-3xl mx-auto">
          {/* Score Badge */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-8"
          >
            <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full ${content.bgColor} ${content.borderColor} border`}>
              <span className={`text-sm font-medium ${content.color}`}>
                Score : {score}/100 • Profil {profile}
              </span>
            </div>
          </motion.div>

          {/* Title Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-center mb-10"
          >
            <h1 className="text-2xl sm:text-4xl font-heading font-bold mb-4">
              {content.title}
            </h1>
            <p className={`text-lg sm:text-xl ${content.color} font-medium`}>
              {content.subtitle}
            </p>
          </motion.div>

          {/* Body Text */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="space-y-4 mb-10 text-muted text-base sm:text-lg"
          >
            {content.body.map((paragraph, index) => (
              <p key={index}>{paragraph}</p>
            ))}
          </motion.div>

          {/* Action Plan */}
          {content.actionPlan && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.25 }}
              className="bg-card/50 border border-border rounded-2xl p-6 sm:p-8 mb-10"
            >
              <h3 className={`text-xl font-bold mb-6 ${content.color}`}>
                {content.actionPlan.title}
              </h3>
              <div className="space-y-5">
                {content.actionPlan.steps.map((step, index) => (
                  <div key={index} className="flex gap-4">
                    <div className={`flex-shrink-0 w-8 h-8 rounded-full ${content.bgColor} ${content.borderColor} border flex items-center justify-center`}>
                      <span className={`font-bold text-sm ${content.color}`}>{index + 1}</span>
                    </div>
                    <div>
                      <h4 className="font-semibold mb-1">{step.title.replace(/^\d+\.\s*/, '')}</h4>
                      <p className="text-muted text-sm">{step.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          )}

          {/* Promo Box */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.3 }}
            className="bg-card border border-primary/30 rounded-2xl p-6 sm:p-8 mb-10"
          >
            {!isExpired ? (
              <>
                <div className="flex items-center justify-center gap-2 mb-4">
                  <Gift className="w-6 h-6 text-primary" />
                  <h3 className="text-lg sm:text-xl font-bold">
                    Offre de bienvenue — réservée aux nouveaux membres
                  </h3>
                </div>

                <p className="text-center text-muted mb-4">
                  Utilise le code <span className="text-primary font-bold text-xl">{PROMO_CODE}</span> pour bénéficier de <span className="text-primary font-bold">-20 %</span> sur ton accès à Boost-StrategIA.
                </p>

                <div className="flex items-center justify-center gap-2 text-orange-400 mb-6">
                  <Clock className="w-5 h-5" />
                  <span className="font-medium">
                    Ce code est valable 48h seulement — il expire dans {formatTime(timeLeft)}
                  </span>
                </div>

                <div className="text-center">
                  <Link to="/pricing" state={{ fromThankYou: true, promoCode: PROMO_CODE }}>
                    <Button size="lg" className="text-lg px-8 py-6 shadow-glow-lg">
                      Je rejoins Boost-StrategIA avec -20 %
                      <ArrowRight className="ml-2 w-5 h-5" />
                    </Button>
                  </Link>
                  <p className="text-sm text-muted mt-3">
                    (Tu peux annuler à tout moment. Aucun engagement.)
                  </p>
                </div>
              </>
            ) : (
              <>
                <div className="text-center">
                  <p className="text-muted mb-6">
                    L'offre de bienvenue a expiré, mais tu peux toujours rejoindre Boost-StrategIA au tarif normal.
                  </p>
                  <Link to="/pricing">
                    <Button size="lg" className="text-lg px-8 py-6">
                      Découvrir les offres
                      <ArrowRight className="ml-2 w-5 h-5" />
                    </Button>
                  </Link>
                </div>
              </>
            )}
          </motion.div>

          {/* Testimonials (only for Prédictible) */}
          {content.testimonials && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="mb-10"
            >
              <h3 className="text-xl font-bold text-center mb-6">
                Ce qu'ils ont vécu. Dans leurs mots.
              </h3>
              <div className="space-y-4">
                {content.testimonials.map((testimonial, index) => (
                  <div key={index} className="bg-card/50 border border-border rounded-xl p-6">
                    <Quote className="w-8 h-8 text-primary/30 mb-3" />
                    <p className="text-muted italic mb-4">"{testimonial.quote}"</p>
                    <div>
                      <p className="font-semibold">{testimonial.author}</p>
                      <p className="text-sm text-muted">{testimonial.role}</p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          )}

          {/* What is Boost-StrategIA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="bg-card/30 border border-border rounded-2xl p-6 sm:p-8"
          >
            <h3 className="text-xl font-bold mb-4">
              Boost-StrategIA, c'est quoi exactement ?
            </h3>
            <div className="space-y-4 text-muted">
              <p>
                C'est ton copilote marketing IA… mais pas n'importe lequel. Il intègre <span className="text-foreground font-medium">35 ans d'expertise marketing</span>, structurés en <span className="text-foreground font-medium">51 étapes concrètes</span>.
              </p>
              <p>
                Il ne te dit pas juste "ce qui cloche". Il te guide pour construire — étape par étape — la stratégie qui correspond à <span className="text-primary font-medium">TOI</span>. Ton profil. Ton offre. Ton marché.
              </p>
              <p>
                Formateurs, infopreneurs, coachs, thérapeutes… <span className="text-foreground font-medium">Si tu vends ton expertise, il est fait pour toi.</span>
              </p>
            </div>

            {/* Second CTA */}
            {!isExpired && (
              <div className="text-center mt-8">
                <Link to="/pricing" state={{ fromThankYou: true, promoCode: PROMO_CODE }}>
                  <Button size="lg" className="text-lg px-8 shadow-glow-lg">
                    Je rejoins Boost-StrategIA avec -20 %
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
                <p className="text-sm text-muted mt-3">
                  (Tu peux annuler à tout moment. Aucun engagement.)
                </p>
              </div>
            )}
          </motion.div>
        </div>
      </main>
    </div>
  );
};

export default ThankYou;
